import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE, acars } from "@/lib/api";
import { useAudioSettings } from "@/lib/audio-settings";
import { playClip, stopClip, setCabinLevel, useNowPlaying } from "@/lib/cabin-audio";
import { OPERATORS } from "@/lib/branding";
import { Loader2, Music2, Play, Square, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

/**
 * Audio
 * -----
 * The pilot's side of the soundpacks. The admin page uploads clips; this one
 * decides which of them play on their own and lets any of them be fired by hand.
 *
 * Two things worth knowing about the content model:
 *
 * `sounds.PHASES` is the whole vocabulary — eight clips per operator pack, each
 * tied to a flight phase. So the manual board is the same eight clips, not a
 * separate library: there is nothing else in the system to play. If the VA
 * wants standalone announcements ("seatbelts on") with no phase attached, that
 * is a change to the pack model and the admin uploader, not to this page.
 *
 * A pack is per OPERATOR, so what is listed here follows the operator tab, and
 * during a flight the one that matters is the operator of the active flight.
 */

/** Phase -> what actually makes it fire, in the pilot's terms. Taken from
 *  PHASE_TO_SOUND_KEY in lib/sound-order.js; if that mapping changes, so does this. */
const TRIGGER_HINT = {
  BOARDING:   "On INIT, before pushback",
  PUSHBACK:   "When taxi out begins",
  INFLIGHT:   "On reaching the planned cruise level (or level for 10 min below it)",
  TURBULENCE: "Manual only — nothing detects turbulence yet",
  DESCENT:    "At top of descent",
  APPROACH:   "Below 5,000 ft on descent",
  ARRIVAL:    "After touchdown, once slowed below 25 kt to taxi in",
  DEPLANING:  "At the gate, once IN has fired",
};

const pretty = (p) => p.charAt(0) + p.slice(1).toLowerCase();

export default function Audio() {
  const qc = useQueryClient();
  const [{ volume, muted }, setAudio] = useAudioSettings();
  // What the shared cabin channel is playing — kept by lib/cabin-audio.js, not
  // by this page, so a clip started here plays on after the pilot navigates
  // away and its button is still lit when they come back.
  const playing = useNowPlaying();

  const { data: active } = useQuery({ queryKey: ["acars-active"], queryFn: acars.active });
  const flightOperator = (active?.operator || "").toUpperCase() || null;

  const [operator, setOperator] = useState(flightOperator || "AC");
  const shownOperator = operator;
  // Show the division name, never the raw scheduling code — "a AC flight" is
  // both jargon and the wrong article. Falls back to the code for an operator
  // that is not in OPERATORS (an older soundpack, say), which is better than
  // rendering "undefined".
  const shownOperatorName =
    OPERATORS.find((o) => o.code === shownOperator)?.name || shownOperator;

  const { data: phases } = useQuery({
    queryKey: ["sound-phases"],
    queryFn: () => api.get("/sounds/phases").then((r) => r.data.phases),
    staleTime: 60 * 60 * 1000,
  });

  const { data: pack, isLoading: packLoading } = useQuery({
    queryKey: ["operator-pack", shownOperator],
    queryFn: () => api.get(`/sounds/packs/for-operator/${shownOperator}`).then((r) => r.data),
    enabled: !!shownOperator,
    staleTime: 60_000,
  });

  const { data: prefs } = useQuery({
    queryKey: ["pilot-prefs"],
    queryFn: () => api.get("/pilot/prefs").then((r) => r.data),
  });

  const savePrefs = useMutation({
    mutationFn: (sound_auto) => api.put("/pilot/prefs", { sound_auto }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pilot-prefs"] }),
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save — the switch has been put back"),
  });

  // Unset means ON. Only phases the pilot has changed are stored, so a phase
  // added to PHASES later is on for everyone rather than arriving muted.
  const autoOn = (phase) => prefs?.sound_auto?.[phase] !== false;

  const toggle = (phase) => {
    const next = { ...(prefs?.sound_auto || {}), [phase]: !autoOn(phase) };
    // Optimistic: the switch should move under the finger, and onError above
    // re-reads the server copy so a failed save visibly snaps back.
    qc.setQueryData(["pilot-prefs"], (old) => ({ ...(old || {}), sound_auto: next }));
    savePrefs.mutate(next);
  };

  const clips = useMemo(() => {
    const list = phases || [];
    return list.map((p) => ({ phase: p, meta: pack?.sounds?.[p] || null }));
  }, [phases, pack]);

  const withClips = clips.filter((c) => c.meta?.filename);

  const stop = () => stopClip();

  // Apply volume/mute to the clip that is ALREADY playing, not just at the
  // moment play() is called; dragging the slider mid-preview used to do
  // nothing until the next play.
  useEffect(() => {
    setCabinLevel({ volume, muted });
  }, [volume, muted]);

  const play = (phase, meta) => {
    if (!meta?.filename) return;
    if (playing === phase) return stop();
    playClip(`${API_BASE}/api/sounds/file/${meta.filename}`, phase).catch(() => {
      // Autoplay policy blocks playback until the page has been interacted
      // with. This IS a click, so a rejection here is a real failure — a
      // missing file, a codec the browser will not take — and silence with
      // no explanation is the thing this project keeps removing.
      toast.error(`Could not play ${pretty(phase)} — the file may be missing or in an unsupported format`);
    });
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="audio-page">
      <div className="mb-6 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="overline mb-1">Cabin</div>
          <h1 className="text-4xl font-bold tracking-tight">Announcements</h1>
          <div className="acbar" />
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
            Clips play automatically as your flight moves through each phase.
            Switch any of them off, or trigger one by hand at any time.
          </p>
        </div>

        {/* Master volume — the same setting the sidebar slider writes, so the
            two stay in step. */}
        <div className="flex items-center gap-3 card-flat px-4 py-3" data-testid="audio-volume">
          <button
            type="button"
            onClick={() => setAudio({ muted: !muted })}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range" min="0" max="1" step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => setAudio({ volume: Number(e.target.value), muted: false })}
            className="w-40 accent-primary"
            aria-label="Volume"
            data-testid="audio-volume-slider"
          />
          <span className="text-xs font-mono text-muted-foreground w-10 text-right">
            {Math.round((muted ? 0 : volume) * 100)}%
          </span>
        </div>
      </div>

      {/* Operator tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-6" data-testid="audio-operators">
        {OPERATORS.filter((o) => o.code !== "OTHER").map((o) => {
          const on = o.code === shownOperator;
          return (
            <button
              key={o.code}
              type="button"
              onClick={() => { stop(); setOperator(o.code); }}
              data-testid={`audio-operator-${o.code}`}
              className={`px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                on ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {o.name}
              {flightOperator === o.code && (
                <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-primary">
                  your flight
                </span>
              )}
            </button>
          );
        })}
      </div>

      {packLoading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading pack…
        </div>
      )}

      {!packLoading && !pack && (
        <div className="card-flat p-8 text-center" data-testid="audio-no-pack">
          <Music2 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <div className="font-semibold mb-1">No active soundpack for this operator</div>
          <div className="text-sm text-muted-foreground">
            Nothing will play on a {shownOperatorName} flight until an admin uploads and
            activates one.
          </div>
        </div>
      )}

      {pack && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Auto-triggers */}
          <section className="card-flat" data-testid="audio-auto">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="overline">Auto-triggers · {pack.name}</div>
              <div className="text-xs font-mono text-muted-foreground">
                {withClips.filter((c) => autoOn(c.phase)).length}/{withClips.length} on
              </div>
            </div>
            <div className="divide-y divide-border">
              {clips.map(({ phase, meta }) => (
                <div key={phase} className="px-5 py-3 flex items-center gap-3" data-testid={`audio-phase-${phase}`}>
                  <button
                    type="button"
                    onClick={() => play(phase, meta)}
                    disabled={!meta?.filename}
                    aria-label={`Preview ${pretty(phase)}`}
                    className="w-8 h-8 shrink-0 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30 disabled:hover:text-muted-foreground disabled:hover:border-border transition-colors"
                  >
                    {playing === phase ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{pretty(phase)}</div>
                    {/* No filename here. An operator names their upload
                        whatever they like, and to a pilot that is noise
                        attached to a phase they already have a label for. The
                        admin page still shows it, because whoever uploaded it
                        needs to know which file is on which phase.

                        The empty case stays: it is why the play button is
                        disabled, and that is a fact the pilot can act on. */}
                    {!meta?.filename && (
                      <div className="text-[11px] font-mono text-muted-foreground">
                        no clip uploaded
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{TRIGGER_HINT[phase]}</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoOn(phase)}
                    aria-label={`Auto-play ${pretty(phase)}`}
                    onClick={() => toggle(phase)}
                    disabled={!meta?.filename}
                    data-testid={`audio-toggle-${phase}`}
                    className={`w-11 h-6 shrink-0 rounded-full relative transition-colors disabled:opacity-30 ${
                      autoOn(phase) && meta?.filename ? "bg-primary" : "bg-secondary border border-border"
                    }`}
                  >
                    <span
                      className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white transition-all ${
                        autoOn(phase) && meta?.filename ? "left-6" : "left-1"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Manual soundboard */}
          <section className="card-flat" data-testid="audio-board">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="overline">Manual soundboard</div>
              {playing && (
                <button
                  type="button"
                  onClick={stop}
                  data-testid="audio-stop"
                  className="text-xs font-mono uppercase tracking-widest text-primary hover:underline"
                >
                  Stop
                </button>
              )}
            </div>
            <div className="p-5">
              {withClips.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  This pack has no clips uploaded yet.
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {withClips.map(({ phase, meta }) => (
                      <button
                        key={phase}
                        type="button"
                        onClick={() => play(phase, meta)}
                        data-testid={`audio-play-${phase}`}
                        className={`flex items-center justify-between gap-2 px-4 py-3 rounded-md border text-sm text-left transition-colors ${
                          playing === phase
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary hover:text-primary"
                        }`}
                      >
                        <span className="truncate">{pretty(phase)}</span>
                        {playing === phase ? <Square className="w-3.5 h-3.5 shrink-0" /> : <Play className="w-3.5 h-3.5 shrink-0" />}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
                    Playing a clip here is local to this machine — it does not
                    announce anything to other pilots, and it does not change
                    what fires automatically.
                  </p>
                </>
              )}
            </div>
          </section>
        </div>
      )}

    </div>
  );
}
